package top.wcpe.allincore.dsl.compiler.parse

import top.wcpe.allincore.dsl.compiler.ast.AnimationAst
import top.wcpe.allincore.dsl.compiler.ast.ComponentAst
import top.wcpe.allincore.dsl.compiler.ast.DslHeaderAst
import top.wcpe.allincore.dsl.compiler.ast.DslMode
import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.ast.ModelAst
import top.wcpe.allincore.dsl.compiler.ast.StateAst
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.lex.TokenType

/**
 * 解析顶层 `component { ... }` 块，组件名由编译入口按资源路径提供。
 */
internal class ComponentParser(
    private val p: Parser,
    private val dslHeader: DslHeaderAst? = null,
) {

    private companion object {
        // 软关键字（词法层未升级为硬关键字，作为 IDENTIFIER 出现）：组件块内显式不支持，给 E325。
        private const val SOFT_KW_OPEN = "open"
        private const val SOFT_KW_CLOSE = "close"
        private const val SOFT_KW_MODEL = "model"
    }

    fun parseComponent(): ComponentAst {
        val componentTok = p.expect(TokenType.KW_COMPONENT, "缺少 component 关键字")

        // 与 screen 一致：不允许出现字符串 id，组件名由 path 推导
        if (p.check(TokenType.STRING)) {
            p.fail("component 后不再允许声明字符串 id；请使用资源路径作为组件名")
        }

        p.expect(TokenType.LBRACE, "component 头部后缺少 '{'")
        p.skipNewlines()

        p.inComponentFile = true
        try {
            var props: List<ComponentAst.PropDecl> = emptyList()
            var propsFound = false
            var root: ElementAst? = null
            val animations = mutableListOf<AnimationAst>()
            var state: StateAst? = null
            var model: ModelAst? = null
            val dslMode = p.currentDslMode

            while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
                when (p.current().type) {
                    TokenType.KW_PROPS -> {
                        if (propsFound) p.fail("props 块只能声明一次")
                        props = PropsBlockParser(p).parse()
                        propsFound = true
                    }
                    TokenType.KW_ANIMATION -> {
                        animations += AnimationBlockParser(p).parse()
                    }
                    TokenType.KW_ROOT -> {
                        if (root != null) p.fail("root 块只能声明一次")
                        root = ElementBlockParser(p, dslMode).parseRoot()
                    }
                    // 计划 04 里程碑 4：组件级 `state defaults { ... }`，每实例独立隔离（展开期提升 + 改名）。
                    TokenType.KW_STATE -> {
                        if (dslMode == DslMode.STRICT) {
                            p.fail(DslError.AUI_V2_STRICT_FORBIDS_LEGACY_SYNTAX, p.current().position, "state defaults")
                        }
                        if (state != null) p.fail("state 块只能声明一次")
                        state = StateBlockParser(p).parse()
                    }
                    // 决策点 D：组件内仅支持 state，session / persist / derived / fun 显式不支持。
                    TokenType.KW_SESSION -> p.fail(DslError.E325_COMPONENT_UNSUPPORTED_BLOCK, p.current().position, "session")
                    TokenType.KW_PERSIST -> p.fail(DslError.E325_COMPONENT_UNSUPPORTED_BLOCK, p.current().position, "persist")
                    TokenType.KW_DERIVED -> p.fail(DslError.E325_COMPONENT_UNSUPPORTED_BLOCK, p.current().position, "derived")
                    TokenType.KW_FUN -> p.fail(DslError.E325_COMPONENT_UNSUPPORTED_BLOCK, p.current().position, "fun")
                    else -> {
                        val cur = p.current()
                        // open / close 是软关键字（以 IDENTIFIER 出现）：组件内同样显式不支持，给精确的 E325。
                        when (cur.lexeme) {
                            SOFT_KW_MODEL -> {
                                if (dslMode == DslMode.LEGACY) {
                                    p.fail(DslError.AUI_V2_LEGACY_FORBIDS_V2_SYNTAX, cur.position, "model")
                                }
                                if (model != null) p.fail("model 块只能声明一次", cur.position)
                                val parsedModel = ModelBlockParser(p).parse(component = true)
                                model = parsedModel
                                state = parsedModel.state
                            }
                            SOFT_KW_OPEN -> p.fail(DslError.E325_COMPONENT_UNSUPPORTED_BLOCK, cur.position, "open")
                            SOFT_KW_CLOSE -> p.fail(DslError.E325_COMPONENT_UNSUPPORTED_BLOCK, cur.position, "close")
                            else -> p.fail("component 块内不识别的 token: '${cur.lexeme}'")
                        }
                    }
                }
                p.skipNewlines()
            }

            val closeToken = p.expect(TokenType.RBRACE, "component 块缺少 '}'")
            p.skipNewlines()
            p.expect(TokenType.EOF, "component 块之后存在多余内容")

            if (root == null) p.fail("component 必须包含 root 块", componentTok.position)

            return ComponentAst(
                id = p.screenId,
                props = props,
                root = root,
                animations = animations,
                state = state,
                dslHeader = dslHeader,
                model = model,
                position = p.completePosition(componentTok.position, closeToken),
            )
        } finally {
            p.inComponentFile = false
        }
    }
}
